import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/apiClient";
import { useCurrentUser } from "@/lib/auth";
import { formatIsoDateShort, todayLocal } from "@/lib/bookingDates";
import { canWriteGuestPitchBooking } from "@/lib/permissions";
import { usePageHeader } from "@/layout/usePageHeader";
import { Alert } from "@/components/ui/Alert";
import { OnSiteGuestsBoard } from "@/components/bookings/OnSiteGuestsBoard";
import { useGuestById } from "@/lib/useGuestById";
import { GuestStayModal } from "@/components/bookings/GuestStayModal";
import { EditBookingModal } from "@/pages/bookings/EditBookingModal";
import type { BookingDeparture, Pitch, PitchCategory } from "@/types/camping";

export function OnSiteGuestsPage() {
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canEdit = canWriteGuestPitchBooking(groups);
  const today = todayLocal();

  const [bookings, setBookings] = useState<BookingDeparture[]>([]);
  const [pitches, setPitches] = useState<Pitch[]>([]);
  const [categories, setCategories] = useState<PitchCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stayModalBooking, setStayModalBooking] = useState<BookingDeparture | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<BookingDeparture | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get<BookingDeparture[]>(`/api/v1/bookings/on-site?reference_date=${today}`),
      api.get<Pitch[]>("/api/v1/pitches"),
      api.get<PitchCategory[]>("/api/v1/pitch-categories").catch(() => [] as PitchCategory[]),
    ])
      .then(([b, p, c]) => {
        setBookings(b);
        setPitches(p);
        setCategories(c);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [today]);

  useEffect(() => {
    setPageMeta({
      title: "Gäste am Platz",
      subtitle: `Eingecheckte Gäste am ${formatIsoDateShort(today)}`,
    });
    load();
    return () => setPageMeta(null);
  }, [load, setPageMeta, today]);

  const stayModalGuest = useGuestById(stayModalBooking?.guest_id);
  const stayModalPitch = useMemo(
    () =>
      stayModalBooking?.pitch_id
        ? pitches.find((p) => p.id === stayModalBooking.pitch_id) ?? null
        : null,
    [stayModalBooking, pitches],
  );
  const selectedBookingGuest = useGuestById(selectedBooking?.guest_id);

  return (
    <div className="space-y-4">
      {error ? (
        <Alert variant="error" title="Liste nicht geladen">
          {error}
        </Alert>
      ) : null}

      {error && bookings.length === 0 ? null : (
        <OnSiteGuestsBoard
          bookings={bookings}
          pitches={pitches}
          categories={categories}
          today={today}
          loading={loading}
          onOpen={setStayModalBooking}
        />
      )}

      <GuestStayModal
        open={!!stayModalBooking}
        booking={stayModalBooking}
        guest={stayModalGuest}
        pitch={stayModalPitch}
        canEdit={canEdit}
        userGroups={groups}
        onInvoiceChanged={load}
        onClose={() => setStayModalBooking(null)}
        onEditBooking={() => {
          if (stayModalBooking) {
            setSelectedBooking(stayModalBooking);
            setStayModalBooking(null);
          }
        }}
        onOpenInvoice={() => {
          if (stayModalBooking) {
            setSelectedBooking(stayModalBooking);
            setStayModalBooking(null);
          }
        }}
      />

      <EditBookingModal
        open={!!selectedBooking}
        booking={selectedBooking}
        guest={selectedBookingGuest}
        pitches={pitches}
        categories={categories}
        canEdit={canEdit}
        onClose={() => setSelectedBooking(null)}
        onSaved={() => {
          setSelectedBooking(null);
          load();
        }}
      />
    </div>
  );
}
